import { Fragment, type ReactNode } from "react";
import type { RichTextNode } from "../types.ts";

const markTags: Record<string, keyof React.JSX.IntrinsicElements> = {
  bold: "strong",
  italic: "em",
  underline: "u",
  code: "code",
  superscript: "sup",
  subscript: "sub",
};

const renderText = (node: RichTextNode): ReactNode =>
  (node.marks ?? []).reduce<ReactNode>((children, mark) => {
    const Tag = markTags[mark.type];
    return Tag ? <Tag>{children}</Tag> : children;
  }, node.value ?? "");

interface RenderOptions {
  /** render paragraphs as spans so rich text can sit inside headings (<h1><p> is invalid) */
  inline: boolean;
  /** already inside a hyperlink; nested <a> is invalid and breaks hydration */
  inLink: boolean;
}

const renderNodes = (nodes: RichTextNode[] = [], options: RenderOptions): ReactNode =>
  nodes.map((node, index) => (
    <Fragment key={index}>{renderNode(node, options)}</Fragment>
  ));

const renderNode = (node: RichTextNode, options: RenderOptions): ReactNode => {
  if (node.nodeType === "hyperlink") {
    const children = renderNodes(node.content, { ...options, inLink: true });
    return options.inLink ? children : <a href={node.data?.uri}>{children}</a>;
  }

  const children = renderNodes(node.content, options);
  switch (node.nodeType) {
    case "document":
      return children;
    case "text":
      return renderText(node);
    case "paragraph":
      return options.inline ? <span className="rt-line">{children}</span> : <p>{children}</p>;
    case "unordered-list":
      return <ul>{children}</ul>;
    case "ordered-list":
      return <ol>{children}</ol>;
    case "list-item":
      return <li>{children}</li>;
    case "heading-1":
    case "heading-2":
    case "heading-3":
    case "heading-4": {
      const Tag = `h${node.nodeType.slice(-1)}` as "h1";
      return <Tag>{children}</Tag>;
    }
    case "hr":
      return <hr />;
    default:
      return children;
  }
};

export default function RichText({
  document,
  inline = false,
}: {
  document?: RichTextNode;
  inline?: boolean;
}) {
  if (!document) return null;
  return <>{renderNode(document, { inline, inLink: false })}</>;
}
